import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FilterPanel = ({ onFilterChange, activeFilters }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const filterCategories = [
    {
      id: 'type',
      label: 'Destination Type',
      icon: 'MapPin',
      options: [
        { value: 'featured', label: 'Featured Destinations', icon: 'Star' },
        { value: 'hidden', label: 'Hidden Gems', icon: 'Sparkles' },
        { value: 'beach', label: 'Beach Paradise', icon: 'Waves' },
        { value: 'mountain', label: 'Mountain Retreat', icon: 'Mountain' },
      ],
    },
    {
      id: 'services',
      label: 'Services',
      icon: 'Building2',
      options: [
        { value: 'hotels', label: 'Hotels & Resorts', icon: 'Hotel' },
        { value: 'restaurants', label: 'Restaurants', icon: 'UtensilsCrossed' },
        { value: 'transport', label: 'Transportation', icon: 'Car' },
        { value: 'activities', label: 'Activities', icon: 'Ticket' },
      ],
    },
    {
      id: 'price',
      label: 'Price Range',
      icon: 'Wallet',
      options: [
        { value: 'budget', label: 'Budget (₱1,000 - ₱3,000)', icon: 'CircleDollarSign' },
        { value: 'moderate', label: 'Moderate (₱3,000 - ₱7,000)', icon: 'CircleDollarSign' },
        { value: 'luxury', label: 'Luxury (₱7,000+)', icon: 'CircleDollarSign' },
      ],
    },
    {
      id: 'hotelFeatures',
      label: 'Hotel Features',
      icon: 'Hotel',
      options: [
        { value: 'beachfront', label: 'Beachfront', icon: 'Waves' },
        { value: 'pool', label: 'Swimming Pool', icon: 'Droplet' },
        { value: 'spa', label: 'Spa & Wellness', icon: 'Sparkles' },
        { value: 'gym', label: 'Fitness Center', icon: 'Dumbbell' },
        { value: 'wifi', label: 'Free WiFi', icon: 'Wifi' },
        { value: 'parking', label: 'Free Parking', icon: 'ParkingCircle' },
        { value: 'petFriendly', label: 'Pet Friendly', icon: 'Dog' },
        { value: 'airportShuttle', label: 'Airport Shuttle', icon: 'Plane' },
        { value: 'roomService', label: '24/7 Room Service', icon: 'Bell' },
        { value: 'oceanView', label: 'Ocean View', icon: 'Eye' },
        { value: 'familyRooms', label: 'Family Rooms', icon: 'Users' },
        { value: 'businessCenter', label: 'Business Center', icon: 'Briefcase' },
      ],
    },
    {
      id: 'restaurantFeatures',
      label: 'Restaurant Features',
      icon: 'UtensilsCrossed',
      options: [
        { value: 'alFresco', label: 'Al Fresco Dining', icon: 'TreePine' },
        { value: 'beachfrontDining', label: 'Beachfront Dining', icon: 'Waves' },
        { value: 'petFriendlyDining', label: 'Pet Friendly', icon: 'Dog' },
        { value: 'liveMusic', label: 'Live Music', icon: 'Music' },
        { value: 'rooftop', label: 'Rooftop Dining', icon: 'Building' },
        { value: 'privateRooms', label: 'Private Dining Rooms', icon: 'DoorClosed' },
        { value: 'vegetarian', label: 'Vegetarian Options', icon: 'Leaf' },
        { value: 'vegan', label: 'Vegan Options', icon: 'Salad' },
        { value: 'halal', label: 'Halal Certified', icon: 'CheckCircle' },
        { value: 'kidsMenu', label: 'Kids Menu', icon: 'Baby' },
        { value: 'barLounge', label: 'Bar & Lounge', icon: 'Wine' },
        { value: 'delivery', label: 'Delivery Available', icon: 'Bike' },
      ],
    },
  ];

  const handleFilterToggle = (value) => {
    const newFilters = activeFilters?.includes(value)
      ? activeFilters?.filter(f => f !== value)
      : [...activeFilters, value];
    onFilterChange(newFilters);
  };

  const clearAllFilters = () => {
    onFilterChange([]);
  };

  return (
    <div className="w-full lg:w-52 bg-card rounded-xl shadow-lg border border-border overflow-hidden">
      {/* Header */}
      <div className="p-1.5 md:p-2 border-b border-border">
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="SlidersHorizontal" size={9} color="var(--color-primary)" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground text-xs">Filters</h3>
              <p className="text-[7px] text-muted-foreground">
                {activeFilters?.length} active
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="lg:hidden p-0.5 rounded-lg hover:bg-muted transition-all duration-300"
          >
            <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={10} />
          </button>
        </div>

        {activeFilters?.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            fullWidth
            onClick={clearAllFilters}
            iconName="X"
            iconPosition="left"
            className="h-5 px-1.5 text-xs"
          >
            Clear All Filters
          </Button>
        )}
      </div>
      {/* Filter Categories */}
      <div className={`${isExpanded ? 'block' : 'hidden lg:block'} max-h-[calc(100vh-200px)] overflow-y-auto`}>
        {filterCategories?.map((category, categoryIndex) => (
          <div
            key={category?.id}
            className={`p-1.5 md:p-2 ${categoryIndex !== filterCategories?.length - 1 ? 'border-b border-border' : ''}`}
          >
            <div className="flex items-center gap-0.5 mb-1.5">
              <Icon name={category?.icon} size={9} color="var(--color-primary)" />
              <h4 className="font-semibold text-foreground text-xs">{category?.label}</h4>
            </div>

            <div className="grid grid-cols-2 gap-0.5">
              {category?.options?.map((option) => {
                const isActive = activeFilters?.includes(option?.value);
                return (
                  <button
                    key={option?.value}
                    onClick={() => handleFilterToggle(option?.value)}
                    className={`flex items-center gap-0.5 p-0.5 rounded-lg transition-all duration-300 ${
                      isActive
                        ? 'bg-primary text-primary-foreground shadow-md'
                        : 'bg-muted hover:bg-muted/80'
                    }`}
                  >
                    <div className={`w-2.5 h-2.5 rounded border flex items-center justify-center flex-shrink-0 ${
                      isActive ? 'border-white bg-white' : 'border-border'
                    }`}>
                      {isActive && (
                        <Icon name="Check" size={7} color="var(--color-primary)" />
                      )}
                    </div>
                    <span className="flex-1 text-left font-medium break-words text-xs">
                      {option?.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {/* Footer */}
      <div className="p-1.5 md:p-2 bg-muted/50">
        <div className="flex items-center gap-0.5 text-[7px] text-muted-foreground">
          <Icon name="Info" size={7} />
          <p>Filters update results in real-time</p>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;